import React from 'react';
import { toast } from 'sonner';
import TaskForm from './TaskForm';
import TaskItem from './TaskItem';
import type { Task } from '../../lib/db';

interface TaskListDesktopProps {
  tasks: Task[];
  taskEstimates: Map<string, number>;
  highlightTaskId?: string;
  shouldHighlightTask?: boolean;
  showTaskDetails: string | null;
  selectedTask: Task | null;
  onSelectTask: (task: Task) => void;
  onCloseDetails: () => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => Promise<void>;
  onTaskDelete: (taskId: string) => Promise<void>;
  onTimeEntry: (task: Task) => void;
  onReactivate: (taskId: string) => Promise<void>;
  onMakeLive: (taskId: string) => Promise<void>;
}

/**
 * Desktop version of the task list. The logic stays here so the main file stays readable.
 * Non-technical summary: we render each task row with edit controls and expanders.
 */
export function TaskListDesktop({
  tasks,
  taskEstimates,
  highlightTaskId,
  shouldHighlightTask,
  showTaskDetails,
  selectedTask,
  onSelectTask,
  onCloseDetails,
  onTaskUpdate,
  onTaskDelete,
  onTimeEntry,
  onReactivate,
  onMakeLive
}: TaskListDesktopProps) {
  if (!tasks.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
        No tasks available yet.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {tasks.map((task) => (
        <div
          key={task.id}
          className={highlightTaskId === task.id && shouldHighlightTask ? 'ring-2 ring-blue-500 rounded-lg' : ''}
        >
          <TaskItem
            task={task}
            onEdit={() => onSelectTask(task)}
            onDelete={() => { void onTaskDelete(task.id); }}
            onTimeEntry={() => onTimeEntry(task)}
            onReactivate={() => { void onReactivate(task.id); }}
            onMakeLive={() => { void onMakeLive(task.id); }}
            hasActiveTimer={false}
            estimatedMinutes={taskEstimates.get(`${task.title}|${task.description || ''}`)}
          />
          {showTaskDetails === task.id && (
            <div className="ml-8 mt-2 p-4 bg-gray-50 rounded-lg">
              <TaskForm
                task={selectedTask || task}
                onSubmit={async (updates) => {
                  try {
                    await onTaskUpdate(task.id, updates);
                    onCloseDetails();
                  } catch (error) {
                    console.error('Error updating task:', error);
                    toast.error('Failed to update task. Please try again.');
                  }
                }}
                onDelete={async () => {
                  await onTaskDelete(task.id);
                  onCloseDetails();
                }}
                onCancel={onCloseDetails}
                availableTasks={tasks}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
